import React, { useState, useEffect } from 'react';
import { Search, Download, Calendar, Building, FileText } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { useOrganization } from '../hooks/useOrganization';

const VendorPayable = () => {
  const { organization } = useOrganization();
  const [payables, setPayables] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branchId: '',
    from: '',
    to: '',
    search: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBranches();
    fetchPayables();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchPayables();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [filters]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/jobcard-branches');
      setBranches(response.data);
    } catch (error) {
    }
  };

  const fetchPayables = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/reports/vendor-payable-detailed?${params}`);
      setPayables(response.data);
    } catch (error) {
      toast.error('Failed to fetch vendor payables');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      params.append('format', 'csv');

      const response = await axios.get(`/api/reports/vendor-payable-export?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendor-payable-report.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    const code = organization?.currency_code || 'AED';
    const numAmount = parseFloat(amount) || 0;
    return `${code} ${numAmount.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Vendor Payable Report</h1>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building className="w-4 h-4 inline mr-1" />
              Branch
            </label>
            <select
              value={filters.branchId}
              onChange={(e) => handleFilterChange('branchId', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Branches</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Order No, Vendor, Contact"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Export Button */}
          <div className="flex items-end">
            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </button>
          </div>
        </div>
      </div>

      {/* Payables Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : payables.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No data available</p>
            <p className="text-sm text-gray-400 mt-1">
              Try adjusting your filters or date range
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order No / PO No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Credit Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Made</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Running Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payables.map((payable) => (
                  <tr key={payable.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {payable.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {payable.contact}
                    </td>
                    <td className="px-6 py-4 text-sm text-blue-600 font-medium">
                      {payable.orderNo}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(payable.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {payable.dueCreditDays} days
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {formatCurrency(payable.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      {formatCurrency(payable.paymentMade)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <span className={payable.runningBalance > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatCurrency(payable.runningBalance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="5" className="px-6 py-3 text-sm font-medium text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    {formatCurrency(payables.reduce((sum, p) => sum + p.amount, 0))}
                  </td>
                  <td className="px-6 py-3 text-sm font-medium text-green-600">
                    {formatCurrency(payables.reduce((sum, p) => sum + p.paymentMade, 0))}
                  </td>
                  <td className="px-6 py-3 text-sm font-medium text-red-600">
                    {formatCurrency(payables.reduce((sum, p) => sum + p.runningBalance, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorPayable;
